import type { Post } from '@/stores/auth-store'
import type { PolicyFeature } from '@/services/policy-features'
import { withSystemRoleCapabilityFallback } from '@/lib/route-access'

export type SidebarCapabilities = NonNullable<Post['capabilities']>

const USER_DIRECTORY_FALLBACK_EXCLUSIONS = [
  'can_view_user_directory',
  'can_access_users',
  'can_access_user_directory',
  'can_create_users',
  'can_update_users',
  'can_approve_users',
  'can_delete_users',
  'can_manage_user_lifecycle',
  'can_unlock_users',
  'can_assign_user_roles',
  'can_remove_user_roles',
] as const

const asBooleanCapabilityFlag = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return value === 1
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'enabled'
  }

  return false
}

const FEATURE_NAME_TO_CAPABILITY_KEY: Record<string, string> = {
  jobcategory: 'can_access_job_categories',
  workdayentry: 'can_access_workdays',
  audit: 'can_access_job_audit',
  credentials: 'can_manage_credentials',
  templates: 'can_manage_templates',
  chat: 'can_manage_communication_chat',
  email: 'can_manage_communication_email',
  sms: 'can_manage_communication_sms',
  whatsapp: 'can_manage_communication_whatsapp',
  slack: 'can_manage_communication_slack',
  role: 'can_manage_roles',
  features: 'can_manage_features',
  workerattendance: 'can_access_worker_attendance',
  expensecategory: 'can_access_expense_categories',
  expensepayments: 'can_access_expense_payments',
  membershipPayments: 'can_manage_membership_payments',
  membershipplan: 'can_manage_membership_plans',
  membershipsubscription: 'can_manage_membership_subscriptions',
  client: 'can_view_vendor_directory',
  supplier: 'can_view_supplier_directory',
  businesscustomers: 'can_view_business_customer_directory',
  retailcustomers: 'can_view_retail_customer_directory',
  announcements: 'can_access_announcements',
}

const FEATURE_UNIQUE_ID_TO_CAPABILITY_KEY: Record<string, string> = {
  fus5007: 'can_access_job_categories',
  fus5015: 'can_access_workdays',
  fus5020: 'can_access_job_audit',
  fuorgcred01: 'can_manage_credentials',
  fustorecred1: 'can_manage_credentials',
  fusvccred01: 'can_manage_credentials',
  fuorgtpl01: 'can_manage_templates',
  fusvctpl01: 'can_manage_templates',
  fuorgcommchat01: 'can_manage_communication_chat',
  fusvccommchat01: 'can_manage_communication_chat',
  fuorgcommemail01: 'can_manage_communication_email',
  fusvccommemail01: 'can_manage_communication_email',
  fuorgcommsms01: 'can_manage_communication_sms',
  fusvccommsms01: 'can_manage_communication_sms',
  fuorgcommwhatsapp01: 'can_manage_communication_whatsapp',
  fusvccommwhatsapp01: 'can_manage_communication_whatsapp',
  fuorgcommslack01: 'can_manage_communication_slack',
  fusvccommslack01: 'can_manage_communication_slack',
  fuo1004: 'can_manage_roles',
  fusvcrole01: 'can_manage_roles',
  fuorgfeat01: 'can_manage_features',
  fustorefeat1: 'can_manage_features',
  fusvcfeat01: 'can_manage_features',
  fusvcworkerattendance01: 'can_access_worker_attendance',
  fus5017: 'can_access_expense_categories',
  fus5018: 'can_access_expense_payments',
  fusvcmembershipplan01: 'can_manage_membership_plans',
  fusvcmembershipsubscription01: 'can_manage_membership_subscriptions',
  fusvcmembershippayments01: 'can_manage_membership_payments',
  fus5008: 'can_view_vendor_directory',
  fus5009: 'can_view_supplier_directory',
  fusvccustomers01: 'can_view_business_customer_directory',
  fusvcrailcust01: 'can_view_retail_customer_directory',
  fusvcannouncements01: 'can_access_announcements',
}

const resolveFeatureCapabilityKey = (feature: PolicyFeature): string => {
  const explicitSidebarKey = typeof feature.sidebarKey === 'string' ? feature.sidebarKey.trim() : ''
  if (explicitSidebarKey) {
    return explicitSidebarKey
  }

  const normalizedFeatureName = String(feature.feature ?? '').trim().toLowerCase()
  const normalizedFeatureId = String(feature.featureUniqueId ?? '').trim().toLowerCase()

  return FEATURE_NAME_TO_CAPABILITY_KEY[normalizedFeatureName]
    ?? FEATURE_UNIQUE_ID_TO_CAPABILITY_KEY[normalizedFeatureId]
    ?? ''
}

export const readCapabilitiesFromRolePermissions = (
  currentRole: Post | null | undefined,
): SidebarCapabilities | undefined => {
  const permissions = currentRole?.roles_data?.permissions
  if (!permissions || typeof permissions !== 'object' || Array.isArray(permissions)) {
    return undefined
  }

  const nextCapabilities: Record<string, boolean> = {}
  let hasCapabilityLikeEntry = false
  for (const [key, value] of Object.entries(permissions as Record<string, unknown>)) {
    if (!key) {
      continue
    }

    if (value !== null && typeof value === 'object') {
      continue
    }

    nextCapabilities[key] = asBooleanCapabilityFlag(value)
    hasCapabilityLikeEntry = true
  }

  if (!hasCapabilityLikeEntry) {
    return undefined
  }

  return nextCapabilities as SidebarCapabilities
}

export const buildEffectiveCapabilitiesFromFeatures = (
  capabilities: SidebarCapabilities | undefined,
  features: PolicyFeature[] | undefined,
): SidebarCapabilities | undefined => {
  if (!capabilities && (!features || features.length === 0)) {
    return capabilities
  }

  const nextCapabilities: Record<string, boolean> = {
    ...(capabilities ?? {}),
  }
  const featuresById = new Map((features ?? []).map((feature) => [feature.id, feature]))

  const isFeatureBlockedByAncestor = (feature: PolicyFeature): boolean => {
    if (feature.branchHidden || feature.disabledByParent) {
      return true
    }

    let parentId = feature.parentId ?? null
    const visited = new Set<number>()
    while (parentId != null && !visited.has(parentId)) {
      visited.add(parentId)
      const parent = featuresById.get(parentId)
      if (!parent) {
        break
      }
      if (parent.branchHidden || parent.disabledByParent) {
        return true
      }
      parentId = parent.parentId ?? null
    }

    return false
  }

  // Multiple catalog rows can share a sidebar_key (e.g. ORG "User" and domain "Users").
  // Grant if ANY matching feature is selected; only clear when every match is off.
  // Never leave "last unselected row wins" clearing a capability granted earlier.
  const featureCapabilityGrant = new Map<string, boolean>()
  for (const feature of features ?? []) {
    const sidebarKey = resolveFeatureCapabilityKey(feature)
    if (sidebarKey.length === 0) {
      continue
    }

    const granted = !(isFeatureBlockedByAncestor(feature) || feature.selected !== true)
    featureCapabilityGrant.set(
      sidebarKey,
      Boolean(featureCapabilityGrant.get(sidebarKey)) || granted,
    )
  }

  for (const [sidebarKey, granted] of featureCapabilityGrant.entries()) {
    nextCapabilities[sidebarKey] = granted
  }

  const customersFeatureBlocked = (features ?? []).some((feature) => {
    const featureName = String(feature.feature ?? '').trim().toLowerCase()
    const featureUniqueId = String(feature.featureUniqueId ?? '').trim().toLowerCase()
    return (featureName === 'customers' || featureUniqueId === 'fusvcustomers01')
      && (feature.branchHidden || feature.disabledByParent || feature.selected !== true || isFeatureBlockedByAncestor(feature))
  })

  if (customersFeatureBlocked || nextCapabilities.can_view_customer_directory === false) {
    nextCapabilities.can_view_customer_directory = false
    nextCapabilities.can_view_retail_customer_directory = false
    nextCapabilities.can_view_business_customer_directory = false
    nextCapabilities.can_access_job_customers = false
  }

  return nextCapabilities as SidebarCapabilities
}

export function canAccessWorkerHotline(
  capabilities?: SidebarCapabilities | null,
): boolean {
  if (capabilities?.can_access_workers === false) {
    return false
  }

  return capabilities?.can_access_worker_hotline === true
}

export const buildEffectiveCapabilities = (
  post: Post | null | undefined,
  features: PolicyFeature[] | undefined,
): SidebarCapabilities | undefined => {
  const capabilitySourceCapabilities = post?.capabilities
  const rolePermissionCapabilities = readCapabilitiesFromRolePermissions(post)

  const capabilityBaseline = (capabilitySourceCapabilities || rolePermissionCapabilities)
    ? {
        ...(rolePermissionCapabilities ?? {}),
        ...(capabilitySourceCapabilities ?? {}),
      } as SidebarCapabilities
    : undefined

  const fromFeatures = buildEffectiveCapabilitiesFromFeatures(
    capabilityBaseline,
    features,
  )

  return withSystemRoleCapabilityFallback(post, fromFeatures, {
    excludeCapabilities: [...USER_DIRECTORY_FALLBACK_EXCLUSIONS],
  }) as SidebarCapabilities | undefined
}

export const buildEffectivePost = (
  post: Post | null | undefined,
  features: PolicyFeature[] | undefined,
): Post | null | undefined => {
  if (!post) {
    return post
  }

  const effectiveCapabilities = buildEffectiveCapabilities(post, features)
  return {
    ...post,
    capabilities: effectiveCapabilities,
  }
}
